// MACHINERY V2 (Stroje) — THE v2 FEEDBACK PRIMITIVE (M2.2).
//
// >>> NEW SHARED V2 PRIMITIVE. M2.3-M2.8 IMPORT THIS — DO NOT REINVENT. <<<
//
// WHY IT EXISTS (M2.2 review finding F2): nothing in the v2 tree mounted a
// notification host. `customerMachinery` sets `error` / `success` on the slice,
// but the component that RENDERS them is the LIVE grid (components/Machinery
// .tsx:1258-1268). So on a v2 page a failed Duplicate, a failed Restore and
// every rejected thunk produced NOTHING — no toast, no card, no console line.
// The user pressed a menu item and the app said nothing at all.
//
// NOT MUI. The obvious shortcut was to reuse shared/components/ActionToast,
// which is MUI — that would violate the locked v2 stack rule (§3) for a
// component we would have to unbuild at M2.12. This is ~90 lines of inline
// styles over the scoped token sheet instead.
//
// NO PORTAL, ON PURPOSE. It renders in-tree under `.machinery-v2-root`, so the
// scoped CSS vars resolve directly and the portal rule (re-carry the scope
// class + transparent background + live palette read) does not apply at all —
// there is no portalled node to theme. `position: fixed` still escapes the
// layout: no ancestor sets transform / filter / perspective, so the fixed
// element's containing block is the viewport and `overflow: hidden` on the
// layout root cannot clip it. zIndex 1500 matches the live ActionToast, above
// Radix dialogs (1300/1301) and MUI dialogs (1300).
//
// REDUCED MOTION IS ALREADY HANDLED — and that is why the entrance is an inline
// `transition` rather than a keyframe: machinery-v2-tokens.scss ends with
// `@media (prefers-reduced-motion: reduce) { .machinery-v2-root, * { animation:
// none !important; transition: none !important } }`, and this component is a
// DESCENDANT of that root, so the transition is neutralised for free. A new
// keyframe would also have meant editing a 288-line sheet that is close to the
// 300-line cap.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useState } from "react";

export type ToastV2Variant = "success" | "error";

export interface ToastV2Props {
  open: boolean;
  variant: ToastV2Variant;
  message: string;
  onClose: () => void;
  /** Auto-dismiss delay in ms. 0 disables auto-dismiss. Default 6000 (the
   *  live ActionToast's duration). */
  duration?: number;
  /** Stack slot, so a success and an error that are open together do not
   *  render on top of each other (0 = topmost). */
  stackIndex?: number;
}

const TONE: Record<ToastV2Variant, { token: string; glyph: React.ReactNode }> = {
  success: {
    token: "--green",
    glyph: (
      <svg viewBox="0 0 12 12" width="13" height="13" aria-hidden>
        <path
          d="M2.4 6.2 L5 8.8 L9.6 3.4"
          stroke="currentColor"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  error: {
    token: "--red",
    glyph: (
      <svg viewBox="0 0 12 12" width="13" height="13" aria-hidden>
        <path d="M6 1.4 L11 10.5 L1 10.5 Z" fill="currentColor" />
        <rect x="5.4" y="4.6" width="1.2" height="3" fill="var(--card)" />
        <rect x="5.4" y="8.2" width="1.2" height="1.2" fill="var(--card)" />
      </svg>
    ),
  },
};

const ToastV2: React.FC<ToastV2Props> = ({
  open,
  variant,
  message,
  onClose,
  duration = 6000,
  stackIndex = 0,
}) => {
  // Drives the entrance transition: mount hidden, flip on the next frame.
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    if (duration <= 0) return () => cancelAnimationFrame(raf);
    const timer = setTimeout(onClose, duration);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [open, duration, onClose]);

  if (!open || !message) return null;

  const tone = TONE[variant];

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      aria-live={variant === "error" ? "assertive" : "polite"}
      style={{
        position: "fixed",
        top: 16 + stackIndex * 66,
        right: 16,
        zIndex: 1500,
        maxWidth: 420,
        minWidth: 240,
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "11px 12px",
        borderRadius: 10,
        background: "var(--card)",
        border: "1px solid color-mix(in oklab, var(" + tone.token + ") 38%, var(--border))",
        borderLeft: "3px solid color-mix(in oklab, var(" + tone.token + ") 82%, var(--ink))",
        boxShadow: "0 12px 32px rgba(15,23,30,0.16), 0 2px 8px rgba(15,23,30,0.08)",
        color: "var(--ink-2)",
        fontSize: 12.5,
        lineHeight: 1.45,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(-6px)",
        transition: "opacity 0.18s ease, transform 0.18s ease",
      }}
    >
      <span
        style={{
          color: "color-mix(in oklab, var(" + tone.token + ") 82%, var(--ink))",
          display: "inline-flex",
          marginTop: 1,
          flexShrink: 0,
        }}
      >
        {tone.glyph}
      </span>
      <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          border: 0,
          background: "transparent",
          color: "var(--faint)",
          cursor: "pointer",
          padding: 0,
          width: 18,
          height: 18,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        &times;
      </button>
    </div>
  );
};

export default ToastV2;
